/*
==============================================================================
Código hecho por Rcrea
Archivo: assets/section-rcrea-guten-product-sticky.js
Sección: Güten Brew | Sticky contextual productos
Versión: trigger Hero Video + compatibilidad Bento + ocultar en footer
Función:
- En producto: mostrar sticky cuando empieza Hero Section Video.
- En home/legacy: mantener compatibilidad con Bento Draft/Ice.
- Ocultar/restaurar header principal sin intervenir menús cuando el sticky no está activo.
- Ocultar sticky automáticamente cuando el footer entra en pantalla.
==============================================================================
*/
(() => {
  const stickySelector = "[data-gbs-context-sticky]";
  const bentoSelector = "[data-gb-bento]";
  const initializedAttribute = "data-gbs-initialized";
  const desktopMedia = window.matchMedia("(min-width: 990px)");

  const unique = (items) => [...new Set(items.filter(Boolean))];

  const normalizeText = (text) =>
    String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const getStickyElements = (root = document) => {
    const elements = [];
    if (root.matches && root.matches(stickySelector)) elements.push(root);
    if (root.querySelectorAll) elements.push(...root.querySelectorAll(stickySelector));
    return unique(elements);
  };

  const isInsideSticky = (element, sticky) => {
    return element === sticky || element.contains(sticky) || sticky.contains(element);
  };

  const isVisible = (element) => {
    if (!element || !document.documentElement.contains(element)) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    if (style.display === "none") return false;
    if (style.visibility === "hidden") return false;
    if (rect.width < 80 || rect.height < 10) return false;
    return true;
  };

  const findHeaderAncestor = (element, sticky) => {
    let current = element;
    let depth = 0;
    let fallback = element;

    while (current && current !== document.body && depth < 9) {
      if (isInsideSticky(current, sticky)) return null;
      if (isVisible(current)) fallback = current;

      const style = window.getComputedStyle(current);
      const rect = current.getBoundingClientRect();

      if (
        (style.position === "fixed" || style.position === "sticky") &&
        rect.width > window.innerWidth * 0.55 &&
        rect.height > 20
      ) {
        return current;
      }

      if (current.classList?.contains("shopify-section-group-header-group")) return current;
      if (current.id === "shopify-section-header") return current;

      current = current.parentElement;
      depth += 1;
    }

    return fallback;
  };

  const resolveHeaders = (sticky) => {
    const manualSelector = (sticky.dataset.mainHeaderSelector || "").trim();

    if (manualSelector) {
      try {
        const manual = unique(
          Array.from(document.querySelectorAll(manualSelector))
            .map((element) => findHeaderAncestor(element, sticky) || element)
            .filter(Boolean)
        );

        if (manual.length) return manual;
      } catch (error) {}
    }

    const selectors = [
      ".shopify-section-group-header-group",
      "#shopify-section-header",
      ".shopify-section-header-sticky",
      "sticky-header",
      "header-component",
      ".section-header",
      ".header-wrapper",
      "header.header",
      "header[role='banner']",
      "[class*='header-sticky']",
      "[class*='sticky-header']"
    ];

    const results = [];

    selectors.forEach((selector) => {
      try {
        document.querySelectorAll(selector).forEach((element) => {
          if (isInsideSticky(element, sticky)) return;

          const target = findHeaderAncestor(element, sticky) || element;
          if (!target || isInsideSticky(target, sticky)) return;

          const rect = target.getBoundingClientRect();
          if (rect.width > window.innerWidth * 0.5 && rect.height > 20) {
            results.push(target);
          }
        });
      } catch (error) {}
    });

    return unique(results);
  };

  const getHeaderHeight = (headers) => {
    let bottom = 0;

    headers.forEach((header) => {
      if (!header || !document.documentElement.contains(header)) return;

      const rect = header.getBoundingClientRect();

      if (rect.top < 180 && rect.bottom > bottom) {
        bottom = rect.bottom;
      }
    });

    return Math.max(0, bottom);
  };

  const prepareHeaders = (headers) => {
    headers.forEach((header) => {
      if (!header || !document.documentElement.contains(header)) return;
      header.classList.add("gbs-header-transition-ready");
    });
  };

  const hideHeaders = (headers, hidden, enabled) => {
    if (!enabled) return;

    headers.forEach((header) => {
      if (!header || !document.documentElement.contains(header)) return;
      header.classList.toggle("gbs-header-hidden-by-context", hidden);
      header.setAttribute("data-gbs-replaced", hidden ? "true" : "false");
    });
  };

  const releaseHeaders = (headers) => {
    headers.forEach((header) => {
      if (!header) return;
      header.classList.remove("gbs-header-hidden-by-context");
      header.classList.remove("gbs-header-transition-ready");
      header.removeAttribute("data-gbs-replaced");
      header.style.removeProperty("opacity");
      header.style.removeProperty("visibility");
      header.style.removeProperty("transform");
      header.style.removeProperty("filter");
    });
  };

  const setStickyVisible = (sticky, active) => {
    sticky.classList.toggle("is-active", active);
    sticky.setAttribute("aria-hidden", active ? "false" : "true");
    document.documentElement.classList.toggle("gbs-context-is-active", active);
  };


  const getProductPurchaseTarget = (sticky) => {
    const manualSelector = (sticky.dataset.productTargetSelector || "").trim();
    const selectors = manualSelector
      ? [manualSelector]
      : [
          "[data-gbs-product-purchase]",
          "[id^='MainProduct-']",
          "[id*='MainProduct']",
          "product-info",
          ".product__info-container",
          ".product__info-wrapper",
          ".main-product",
          "section[id*='main-product']",
          "section[id*='product']",
          ".shopify-section:has(product-info)",
          ".shopify-section:has(form[action*='/cart/add'])",
          "form[action*='/cart/add']"
        ];

    for (const selector of selectors) {
      try {
        const element = document.querySelector(selector);
        if (!element) continue;
        if (isInsideSticky(element, sticky)) continue;

        const target = element.closest?.(".shopify-section") || element;
        if (!target || isInsideSticky(target, sticky)) continue;

        const rect = target.getBoundingClientRect();
        if (rect.width < 120 || rect.height < 80) continue;

        return target;
      } catch (error) {}
    }

    return document.querySelector("main") || document.body;
  };

  const getScrollOffset = (sticky) => {
    const manualOffset = Number.parseFloat(sticky.dataset.productScrollOffset || "");
    if (!Number.isNaN(manualOffset)) return Math.max(0, manualOffset);

    const headers = resolveHeaders(sticky);
    const headerHeight = getHeaderHeight(headers);
    const fallbackHeaderHeight = Number.parseFloat(sticky.dataset.fallbackHeaderHeight || "104") || 104;
    const resolvedHeaderHeight = headerHeight > 0 ? headerHeight : fallbackHeaderHeight;

    return Math.max(0, resolvedHeaderHeight + 14);
  };

  const scrollToProductPurchase = (sticky) => {
    const target = getProductPurchaseTarget(sticky);
    if (!target) return;

    const offset = getScrollOffset(sticky);
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset;

    try {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#gbs-product-purchase`);
    } catch (error) {}

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth"
    });
  };

  const setActivePanel = (sticky, context) => {
    const panels = Array.from(sticky.querySelectorAll("[data-gbs-panel]"));
    if (!panels.length) return;

    let resolvedContext = context;
    if (!resolvedContext || !panels.some((panel) => panel.dataset.gbsPanel === resolvedContext)) {
      resolvedContext = panels[0].dataset.gbsPanel;
    }

    if (sticky.dataset.activeContext === resolvedContext) return;

    sticky.dataset.activeContext = resolvedContext;

    panels.forEach((panel) => {
      const active = panel.dataset.gbsPanel === resolvedContext;
      panel.classList.toggle("is-active", active);
      panel.setAttribute("aria-hidden", active ? "false" : "true");
    });
  };

  const inferContext = (section) => {
    const explicit = section.dataset.gbStickyContext;
    if (explicit && explicit !== "none") return explicit;

    const heading = normalizeText(section.querySelector(".gb-bento__heading")?.textContent);
    if (heading.includes("guten draft")) return "draft";
    if (heading.includes("guten ice")) return "ice";

    return null;
  };

  const getBentoTargets = () => {
    return Array.from(document.querySelectorAll(bentoSelector))
      .map((section) => ({
        section,
        context: inferContext(section)
      }))
      .filter((item) => item.context === "draft" || item.context === "ice")
      .sort((a, b) => {
        if (a.section === b.section) return 0;
        return a.section.compareDocumentPosition(b.section) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });
  };

  const getShopifySectionWrapper = (section) => {
    if (!section) return null;
    return section.closest(".shopify-section") || section;
  };

  const getNextPageSection = (section, sticky) => {
    const wrapper = getShopifySectionWrapper(section);
    if (!wrapper) return null;

    let next = wrapper.nextElementSibling;

    while (next) {
      const containsContextSticky =
        next.matches?.(".section-rcrea-guten-product-sticky") ||
        next.querySelector?.(stickySelector);

      const isEmpty = !next.children.length && !String(next.textContent || "").trim();

      if (!containsContextSticky && !isEmpty && !isInsideSticky(next, sticky)) {
        return next;
      }

      next = next.nextElementSibling;
    }

    return null;
  };

  const hasReachedEndOfBento = (targets, sticky) => {
    if (!targets.length) return true;

    const lastTarget = targets[targets.length - 1].section;
    const lastRect = lastTarget.getBoundingClientRect();
    const stickyHeight = sticky.offsetHeight || 90;
    const nextSection = getNextPageSection(lastTarget, sticky);

    if (nextSection) {
      const nextRect = nextSection.getBoundingClientRect();
      return nextRect.top <= stickyHeight;
    }

    return lastRect.bottom <= stickyHeight;
  };


  const getFooterElement = (sticky) => {
    const manualSelector = (sticky.dataset.footerSelector || "").trim();
    const selectors = manualSelector
      ? [manualSelector]
      : [
          ".shopify-section-group-footer-group",
          "#shopify-section-footer",
          "footer[role='contentinfo']",
          "footer",
          ".footer"
        ];

    for (const selector of selectors) {
      try {
        const matches = Array.from(document.querySelectorAll(selector));

        const footer = matches.find((element) => {
          if (!element || isInsideSticky(element, sticky)) return false;
          if (!document.documentElement.contains(element)) return false;

          const rect = element.getBoundingClientRect();
          const style = window.getComputedStyle(element);

          if (style.display === "none" || style.visibility === "hidden") return false;
          if (rect.width < window.innerWidth * 0.35 || rect.height < 40) return false;

          return true;
        });

        if (footer) return footer;
      } catch (error) {}
    }

    return null;
  };

  const hasReachedFooter = (sticky) => {
    if (sticky.dataset.hideAtFooter === "false") return false;

    const footer = getFooterElement(sticky);
    if (!footer) return false;

    const rect = footer.getBoundingClientRect();
    const offset = Number.parseFloat(sticky.dataset.footerHideOffset || "0") || 0;
    const activationLine = window.innerHeight - offset;

    return rect.top <= activationLine && rect.bottom > 0;
  };

  const getTriggerElement = (sticky) => {
    const selector = (sticky.dataset.triggerSelector || "[data-gb-sticky-trigger='product-hero-video']").trim();

    try {
      return document.querySelector(selector);
    } catch (error) {
      return document.querySelector("[data-gb-sticky-trigger='product-hero-video']");
    }
  };

  const getMode = (sticky) => {
    const configured = sticky.dataset.activationMode || "auto";
    if (configured !== "auto") return configured;

    const trigger = getTriggerElement(sticky);
    const bentoTargets = getBentoTargets();
    const isProductInternalMode = sticky.dataset.useInternalProductScroll === "true";

    /*
      Auto inteligente:
      - Producto: si está activo el scroll interno de compra, prioriza Hero Video.
      - Homepage: si NO está activo el scroll interno, prioriza Bento Draft/Ice para mantener el recorrido original.
      Esto evita que el Hero Video del homepage active el sticky antes de tiempo.
    */
    if (isProductInternalMode && trigger) return "hero_video";
    if (bentoTargets.length) return "bento_sections";
    if (trigger) return "hero_video";

    return "always";
  };

  const initSticky = (sticky) => {
    if (!sticky || sticky.hasAttribute(initializedAttribute)) return;

    sticky.setAttribute(initializedAttribute, "true");

    sticky.querySelectorAll("[data-gbs-scroll-to-product]").forEach((button) => {
      button.addEventListener("click", (event) => {
        if (sticky.dataset.useInternalProductScroll !== "true") return;

        const href = button.getAttribute("href") || "";
        if (!href.startsWith("#")) return;

        event.preventDefault();
        scrollToProductPurchase(sticky);
      });
    });

    const state = {
      sticky,
      headers: [],
      headerHeight: 0,
      active: false,
      ticking: false,
      resizeTimer: null,
      mode: "auto"
    };

    const shouldHideHeader = () => sticky.dataset.hideHeaderWhenActive !== "false";

    const resolve = () => {
      state.headers = resolveHeaders(sticky);
      prepareHeaders(state.headers);

      const measured = getHeaderHeight(state.headers);
      if (measured > 0) {
        state.headerHeight = measured;
      }

      state.mode = getMode(sticky);
    };

    const activate = (context) => {
      if (!state.headers.length) resolve();

      prepareHeaders(state.headers);
      setActivePanel(sticky, context);

      state.active = true;
      setStickyVisible(sticky, true);

      window.setTimeout(() => {
        hideHeaders(state.headers, true, shouldHideHeader());
      }, 40);
    };

    const deactivate = () => {
      state.active = false;
      hideHeaders(state.headers, false, shouldHideHeader());
      setStickyVisible(sticky, false);
    };

    const updateHeroMode = () => {
      const trigger = getTriggerElement(sticky);

      if (!trigger) {
        deactivate();
        return;
      }

      const fallbackHeaderHeight = Number.parseFloat(sticky.dataset.fallbackHeaderHeight || "104") || 104;
      const activationOffset = Number.parseFloat(sticky.dataset.activationOffset || "0") || 0;
      const headerHeight = state.headerHeight > 0 ? state.headerHeight : fallbackHeaderHeight;
      const activationLine = Math.max(0, headerHeight + activationOffset);
      const triggerRect = trigger.getBoundingClientRect();

      const shouldActivate = triggerRect.top <= activationLine;

      if (shouldActivate) {
        activate();
      } else {
        deactivate();
      }
    };

    const updateBentoMode = () => {
      const targets = getBentoTargets();

      if (!targets.length) {
        deactivate();
        return;
      }

      const fallbackHeaderHeight = Number.parseFloat(sticky.dataset.fallbackHeaderHeight || "104") || 104;
      const activationOffset = Number.parseFloat(sticky.dataset.activationOffset || "0") || 0;
      const headerHeight = state.headerHeight > 0 ? state.headerHeight : fallbackHeaderHeight;
      const activationLine = Math.max(0, headerHeight + activationOffset);
      const firstRect = targets[0].section.getBoundingClientRect();
      const reachedEnd = hasReachedEndOfBento(targets, sticky);
      const shouldActivate = firstRect.top <= activationLine && !reachedEnd;

      if (!shouldActivate) {
        deactivate();
        return;
      }

      const stickyHeight = sticky.offsetHeight || 90;
      const switchOffset = Number.parseFloat(sticky.dataset.switchOffset || "0") || 0;
      const switchLine = stickyHeight + switchOffset;

      let current = targets[0];

      targets.forEach((item) => {
        if (item.section.getBoundingClientRect().top <= switchLine) {
          current = item;
        }
      });

      activate(current.context);
    };

    const update = () => {
      const editorPreview = sticky.classList.contains("is-editor-preview");

      if (editorPreview) {
        hideHeaders(state.headers, false, shouldHideHeader());
        setStickyVisible(sticky, true);
        setActivePanel(sticky);
        return;
      }

      if (hasReachedFooter(sticky)) {
        deactivate();
        return;
      }

      state.mode = getMode(sticky);

      if (state.mode === "always") {
        activate();
        return;
      }

      if (state.mode === "hero_video") {
        updateHeroMode();
        return;
      }

      updateBentoMode();
    };

    const requestUpdate = () => {
      if (state.ticking) return;

      state.ticking = true;

      window.requestAnimationFrame(() => {
        update();
        state.ticking = false;
      });
    };

    const handleResize = () => {
      window.clearTimeout(state.resizeTimer);

      state.resizeTimer = window.setTimeout(() => {
        releaseHeaders(state.headers);
        resolve();
        requestUpdate();
      }, 120);
    };

    resolve();
    update();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("orientationchange", handleResize, { passive: true });

    sticky._gbsCleanup = () => {
      window.clearTimeout(state.resizeTimer);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      releaseHeaders(state.headers);
      setStickyVisible(sticky, false);
      state.active = false;
    };
  };

  const destroySticky = (sticky) => {
    if (!sticky) return;

    if (typeof sticky._gbsCleanup === "function") {
      sticky._gbsCleanup();
    }

    sticky.removeAttribute(initializedAttribute);
    delete sticky._gbsCleanup;
  };

  const initAll = (root = document) => {
    getStickyElements(root).forEach(initSticky);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
    window.dispatchEvent(new Event("resize"));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    getStickyElements(event.target).forEach(destroySticky);
  });

  document.addEventListener("shopify:section:select", (event) => {
    getStickyElements(event.target).forEach((sticky) => {
      sticky.classList.add("is-editor-preview");
      const firstPanel = sticky.querySelector("[data-gbs-panel]");
      if (firstPanel) {
        setActivePanel(sticky, firstPanel.dataset.gbsPanel);
      }
      setStickyVisible(sticky, true);
    });
  });

  document.addEventListener("shopify:section:deselect", (event) => {
    getStickyElements(event.target).forEach((sticky) => {
      sticky.classList.remove("is-editor-preview");
      window.dispatchEvent(new Event("resize"));
    });
  });
})();
